import { X } from '@phosphor-icons/react';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '@/lib/i18n';

/** Brifing işareti (uygulama ikonundaki yelpaze), currentColor ile çizilir. */
export function Mark({ size = 20 }: { size?: number }) {
  return (
    <svg viewBox="0 0 590 590" width={size} height={size} aria-hidden="true" fill="currentColor">
      <polygon points="8,175 168,6 12,480" />
      <polygon points="212,104 542,11 20,480" />
      <polygon points="370,248 575,336 23,482" />
      <polygon points="355,448 494,577 28,490" />
    </svg>
  );
}

export function Brand({ compact }: { compact?: boolean }) {
  return (
    <span className="brand">
      <span className="brand-mark">
        <Mark />
      </span>
      {!compact && <span className="brand-text">Brifing</span>}
    </span>
  );
}

/**
 * brifing.si üzerinde harici kapakları Cloudflare görsel dönüştürmesinden
 * geçir (genişliğe göre küçült, WebP/AVIF). Dönüştürme kapalıysa ya da
 * başarısız olursa Img orijinal URL'ye düşer.
 */
// Varsayılan kapalı: Cloudflare Images → Transformations açılmadan yol 404 döner
// (her görsel önce boşa bir istek atar). Açınca VITE_IMAGE_RESIZE=1 ile derle.
const RESIZE = import.meta.env.VITE_IMAGE_RESIZE === '1' && /(^|\.)brifing\.si$/.test(location.hostname);
// Sabit genişlik basamakları: aynı görsel için az sayıda varyant → daha çok
// önbellek isabeti, daha az (ücretli) benzersiz dönüşüm.
const WIDTH_STEPS = [96, 192, 320, 480, 720, 960, 1280];
const SRCSET_WIDTHS = [480, 720, 960, 1280];
function resizedExact(src: string, w: number): string {
  return `/cdn-cgi/image/width=${w},quality=72,format=auto,fit=scale-down/${src}`;
}
function resized(src: string, width: number): string {
  const want = width * Math.min(2, window.devicePixelRatio || 1);
  const w = WIDTH_STEPS.find((s) => s >= want) ?? WIDTH_STEPS[WIDTH_STEPS.length - 1];
  return `/cdn-cgi/image/width=${w},quality=72,format=auto,fit=scale-down/${src}`;
}

/** Kapak görseli: tembel yükleme, kırık görselde kategori renkli zemin + işaret. */
export function Img({
  src,
  alt = '',
  className = '',
  color,
  style,
  eager,
  width = 640,
  label,
  sizes,
}: {
  src: string | null | undefined;
  alt?: string;
  className?: string;
  color?: string;
  style?: CSSProperties;
  eager?: boolean;
  /** Görüntülenen yaklaşık genişlik (px); dönüştürme boyutu buna göre seçilir. */
  width?: number;
  /** Görsel yoksa tipografik kapakta gösterilecek etiket (ör. kategori adı). */
  label?: string;
  /** Verilirse duyarlı srcset kullanılır (manşet; kenar fonksiyonunun preload'uyla aynı set). */
  sizes?: string;
}) {
  const [stage, setStage] = useState(0);
  const clean = src ? src.replace(/&#038;/g, '&') : null;
  const candidates = clean ? (RESIZE && /^https?:\/\//.test(clean) ? [resized(clean, width), clean] : [clean]) : [];
  const current = candidates[stage];
  const failed = !current;
  return (
    <div className={`img-wrap ${className}`} style={{ ...(color ? ({ '--c': color } as CSSProperties) : null), ...style }}>
      {!failed ? (
        <img
          key={current}
          src={current}
          srcSet={sizes && stage === 0 && RESIZE && clean && /^https?:\/\//.test(clean) ? SRCSET_WIDTHS.map((w) => `${resizedExact(clean, w)} ${w}w`).join(', ') : undefined}
          sizes={sizes && stage === 0 && RESIZE ? sizes : undefined}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          fetchPriority={eager ? 'high' : 'auto'}
          decoding="async"
          referrerPolicy={current?.startsWith('/cdn-cgi/') ? 'same-origin' : 'no-referrer'}
          onError={() => setStage((s) => s + 1)}
        />
      ) : (
        <div className={`img-fallback${label ? ' has-label' : ''}`}>
          <Mark />
          {label && <span className="img-fallback-label">{label}</span>}
        </div>
      )}
    </div>
  );
}

export function SourceLogo({ src, site, className = 'src-logo' }: { src?: string | null; site?: string | null; className?: string }) {
  const [stage, setStage] = useState(0);
  let fallback: string | null = null;
  try {
    if (site) fallback = `https://www.google.com/s2/favicons?domain=${new URL(site).hostname}&sz=64`;
  } catch {
    fallback = null;
  }
  const candidates = [src, fallback].filter((u, i, a): u is string => !!u && a.indexOf(u) === i);
  const url = candidates[stage];
  if (!url) return <span className={className} />;
  return <img className={className} src={url} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setStage((s) => s + 1)} />;
}

export function Skeleton({ h = 14, w = '100%', r, style }: { h?: number | string; w?: number | string; r?: number; style?: CSSProperties }) {
  return <div className="sk" style={{ height: h, width: w, borderRadius: r, ...style }} />;
}

export function Empty({ icon, title, body, action }: { icon?: ReactNode; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon && <div className="empty-icon">{icon}</div>}
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {action && <div style={{ marginTop: 18 }}>{action}</div>}
    </div>
  );
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  const t = useT();
  return (
    <Empty
      title={t('err_generic')}
      action={
        onRetry && (
          <button className="btn btn-soft" onClick={onRetry}>
            {t('err_retry')}
          </button>
        )
      }
    />
  );
}

function useEscape(onClose: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
}

/** Diyalog içinde Tab odağını hapseder, kapanınca önceki öğeye geri verir. */
function useFocusTrap(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const node = ref.current;
    if (node && !node.contains(document.activeElement)) {
      (node.querySelector<HTMLElement>('[autofocus], input, textarea, button, [href]') ?? node).focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !node) return;
      const items = [...node.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [ref]);
}

function useScrollLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
}

export function Modal({ onClose, children, labelledBy }: { onClose: () => void; children: ReactNode; labelledBy?: string }) {
  useEscape(onClose);
  useScrollLock();
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref);
  return createPortal(
    <>
      <div className="overlay" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={labelledBy} tabIndex={-1} ref={ref}>
        {children}
      </div>
    </>,
    document.body
  );
}

export function Drawer({ onClose, title, sub, children, foot }: { onClose: () => void; title: string; sub?: string; children: ReactNode; foot?: ReactNode }) {
  const t = useT();
  useEscape(onClose);
  useScrollLock();
  const ref = useRef<HTMLElement>(null);
  useFocusTrap(ref);
  return createPortal(
    <>
      <div className="overlay" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title} ref={ref} tabIndex={-1}>
        <div className="drawer-head">
          <div style={{ flex: 1 }}>
            <h2>{title}</h2>
            {sub && <p className="muted" style={{ fontSize: 13, marginTop: 2 }}>{sub}</p>}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label={t('close')}>
            <X size={20} />
          </button>
        </div>
        <div className="drawer-body">{children}</div>
        {foot && <div className="drawer-foot">{foot}</div>}
      </aside>
    </>,
    document.body
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} className="switch" onClick={() => onChange(!checked)} />;
}

export function Seg<T extends string | number>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  label?: string;
}) {
  return (
    <div className="seg" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} role="tab" aria-selected={o.value === value} className={o.value === value ? 'active' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Avatar({ name, url, size = 34 }: { name?: string | null; url?: string | null; size?: number }) {
  const [failed, setFailed] = useState(false);
  const initial = (name ?? '?').trim().charAt(0).toLocaleUpperCase() || '?';
  if (url && !failed)
    return <img className="avatar" src={url} alt="" style={{ width: size, height: size }} referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: size * 0.42 }} aria-hidden="true">
      {initial}
    </span>
  );
}
